import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { RequestError, Service } from 'webapp-types';
import type { BillingAddonType, BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import joinClasses from 'webapp/helpers/join-classes';
import type { NewItemOption } from 'webapp/components/common/new-item-options';
import CommonRequestTrialButtonComponent from 'webapp/components/common/request-trial-button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanButtonComponent from 'webapp/components/common/upgrade-plan-button';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TemplatesAccountGridItemPlaceholderComponent from 'webapp/components/templates/account/grid-item-placeholder';

interface MainActionOption extends NewItemOption {
	isForCreating?: boolean;
}

export interface EmptyScreensBaseArgs {
	onCreate?: () => void;
	isInfoSectionHidden?: boolean;
}

interface EmptyScreensBaseComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default abstract class EmptyScreensBaseComponent<
	T extends EmptyScreensBaseArgs,
> extends Component<EmptyScreensBaseComponentInterface<T>> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/sources') declare mmSourceService: Service.ModelManager.Sources;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;

	@tracked submitInProgress = false;

	// defining how actions looks like
	// which buttons are shown
	readMoreUrl!: string; // example 'https://help.databox.com';

	titleText!: string;
	descText!: string; // description

	// smaller image on top
	topImageLightUrl!: string;
	topImageDarkUrl!: string;
	topImageHeight = 48; // numeric height

	// bigger image next to description
	imageLightUrl!: string;
	imageDarkUrl!: string;

	// video specs
	videoUrl!: string; // video url

	trialFeatures: BillingFeature[] = [];

	requireAllBillingFeatures = false; // true if all given trialFeatures have to be supported. false if at least one is enough

	get requiredBillingFeature() {
		const allFeatures = this.trialFeatures,
			unsupportedFeatures = allFeatures.filter((f) => !this.billingService.checkIfCan(f));
		if (this.requireAllBillingFeatures || allFeatures.length === unsupportedFeatures.length) {
			return unsupportedFeatures[0] || undefined;
		}
	}

	trialAddonId?: BillingAddonType;

	_noResultImageEl!: JQuery;

	// actions and skeletons
	mainActions!: MainActionOption[];
	skeletonImage?: string;

	extraButton?: {
		isShown: boolean;
		link: string;
		text: string;
		icon: IconName;
	};

	arrowText!: string;

	isArrowInTheMiddle = false;

	abstract trackingOrigin: string;

	get topImageUrl(): string {
		const topImageDarkUrl = this.topImageDarkUrl || this.topImageLightUrl;

		return this.navigationService.theme.isDark ? topImageDarkUrl : this.topImageLightUrl;
	}

	get imageUrl(): string {
		const imageDarkUrl = this.imageDarkUrl || this.imageLightUrl;

		return this.navigationService.theme.isDark ? imageDarkUrl : this.imageLightUrl;
	}

	get targetPlan() {
		const billingFeature = this.requiredBillingFeature;
		if (billingFeature) {
			return (
				this.billingService.getNextPlanForFeatureWithSameBillingCycle(billingFeature) ||
				null
			);
		}
		return null;
	}

	get payingTrialPromotions() {
		const targetPlan = this.targetPlan,
			payingTrialPromotions = targetPlan
				? this.flowPromotionsService.getHigherPlanAutomatedTrialOptionPromotionsForPlanType(
						targetPlan.planType,
					)
				: [];

		return payingTrialPromotions;
	}

	get isTrialAddonPromotionAvailable() {
		if (!this.trialAddonId) {
			return false;
		}

		return !!this.flowPromotionsService.getOptionalTrialAddonPromotion(this.trialAddonId);
	}

	get mustUpgrade() {
		return !!this.requiredBillingFeature;
	}

	get isAnyActionShown() {
		return this.mainActions.length > 0;
	}

	get canRequestTrial(): boolean {
		return this.billingService.canRequestTrial || !!this.payingTrialPromotions.length;
	}

	get canRequestTrialAddon(): boolean {
		return this.billingService.canRequestTrialAddon && !!this.trialAddonId;
	}

	get isCurrentUserViewer(): boolean {
		return !!this.currentUserService.model?.isViewer;
	}

	get isNewSectionShown() {
		return (
			this.isAnyActionShown &&
			!this.mustUpgrade &&
			!this.isCurrentUserViewer &&
			this.isAnySourceConnected
		);
	}

	get isVideoButtonShown(): boolean {
		return (
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins && !!this.videoUrl
		);
	}

	get isReadMoreShown(): boolean {
		return (
			!!this.readMoreUrl && !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
		);
	}

	get isDemoButtonShown(): boolean {
		return this.accountService.isDemoButtonShown && this.mustUpgrade;
	}

	get isBookACallButtonShown(): boolean {
		return (
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			!this.currentUserService.isClientUserInPaidClientSpace
		);
	}

	get isAnySourceConnected() {
		return this.mmSourceService.connectedSourcesCount > 0;
	}

	get enableAddonText() {
		let text = 'Enable';

		if (this.requiredBillingFeature) {
			text += ` ${this.billingService.getFeatureName(this.requiredBillingFeature)}`;
		}

		return text;
	}

	get imageHeight() {
		return this.isNewSectionShown ? 300 : 450;
	}

	get showTemplatesPlaceholder() {
		return false;
	}

	@action
	create() {
		const onCreate = this.args.onCreate;
		if (onCreate) {
			onCreate();
		}
	}

	@action
	watchVideo(): void {
		if (this.videoUrl) {
			this.navigationService.openVideoPopup(this.videoUrl);
		}
	}

	@action
	enableAddon() {
		if (this.isTrialAddonPromotionAvailable) {
			this.flowPromotionsService.openStartAddonTrialPopup(this.requiredBillingFeature);
		} else {
			if (!this.trialAddonId) {
				return;
			}

			if (this.billingService.isTrial) {
				this.submitInProgress = true;

				this.billingService
					.addAddon(this.trialAddonId)
					.then(
						() => {
							this.infolayerService.ok('Addon added!');
						},
						(errorData: RequestError) => {
							this.requestService.handleGenericThrownError(errorData);
						},
					)
					.finally(() => {
						this.submitInProgress = false;
					});
			} else {
				const addon = this.billingService.getAddonInfo(this.trialAddonId);

				if (addon) {
					this.billingService.openAddonPurchasePopup(addon);
				}
			}
		}
	}

	@action
	openDemoAccount() {
		void this.accountService.redirectToDemoAccount(this.trackingOrigin);
	}

	@action
	openBookACall(): void {
		this.analyticsService.event(TrackingEvent.CALL_INITIATED, {
			origin: this.trackingOrigin,
		});
		this.accountService.requestSetupHelpAlternative();
	}
	<template>
		<div
			class={{joinClasses
				(array
					"empty-screen"
					(if this.mustUpgrade "empty-screen--locked")
					(if (has-block) (unless this.mustUpgrade "empty-screen--with-recommended"))
				)
			}}
		>
			{{#if (has-block)}}
				{{#unless this.mustUpgrade}}
					{{yield}}
				{{/unless}}
			{{else if this.isNewSectionShown}}
				<div class="empty-screen__new">
					<ul>
						{{#each this.mainActions as |mainAction|}}
							<li
								role="button"
								class="card card--placeholder-new
									{{if mainAction.isForCreating 'create-new'}}
									flex-item flex-item--placeholder filter-all"
								{{on "click" mainAction.action}}
							>
								<div class="option">
									<CommonUiIconComponent @name={{mainAction.icon}} @size="xl" />
									<span>{{mainAction.text}}</span>
								</div>
							</li>
						{{/each}}
						{{#if this.skeletonImage}}
							<li class="placeholder">
								<img src={{this.skeletonImage}} alt="Skeleton" />
							</li>
						{{/if}}
						{{#if this.showTemplatesPlaceholder}}
							<TemplatesAccountGridItemPlaceholderComponent
								@isBoardAccountTemplate={{true}}
							/>
						{{/if}}
						<li
							class={{joinClasses
								(array
									"info-arrow" (if this.isArrowInTheMiddle "info-arrow--middle")
								)
							}}
						>
							<div class="info-arrow__img">
								<CommonUiIconComponent @name="ArrowLeftUp" @size="2xl" />
							</div>
							<div class="info-arrow__content">{{this.arrowText}}</div>
						</li>
					</ul>
				</div>
			{{/if}}

			{{#unless @isInfoSectionHidden}}
				<div
					class="empty-screen__info
						{{unless this.isNewSectionShown 'empty-screen__info--left'}}"
				>
					{{#if this.isNewSectionShown}}
						<div class="hero">
							<img src={{this.imageUrl}} height={{this.imageHeight}} alt />
						</div>
					{{/if}}

					<div class="title">
						<img src={{this.topImageUrl}} height={{this.topImageHeight}} alt />
						<h2>{{{this.titleText}}}</h2>
					</div>

					<p>{{{this.descText}}}</p>

					<div class="links">
						{{#if this.extraButton.isShown}}
							<a role="button" href={{this.extraButton.link}} target="_blank">
								<CommonUiIconComponent @name={{this.extraButton.icon}} @size="md" />
								{{this.extraButton.text}}
							</a>
						{{/if}}

						{{#if this.isReadMoreShown}}
							<a role="button" href={{this.readMoreUrl}} target="_blank">
								<CommonUiIconComponent @name="Book" @size="md" />
								Learn more
							</a>
						{{/if}}

						{{#if this.isDemoButtonShown}}
							<div class="demo-link">
								or explore in&nbsp;
								<a role="button" {{on "click" this.openDemoAccount}}>
									Databox demo account
									<CommonUiIconComponent @name="External" @size="md" />
								</a>
							</div>
						{{/if}}

						{{#if this.isVideoButtonShown}}
							<a role="button" {{on "click" this.watchVideo}}>
								<CommonUiIconComponent @name="Play" @size="md" />
								Watch demo video
							</a>
						{{/if}}

						{{#if this.isBookACallButtonShown}}
							<a role="button" href="#" {{on "click" this.openBookACall}}>
								<CommonUiIconComponent @name="Calendar" @size="md" />
								Get assistance
							</a>
						{{/if}}
					</div>

					{{#if this.mustUpgrade}}
						<div class="actions">
							{{#if this.canRequestTrialAddon}}
								<SubmitButtonComponent
									@text={{this.enableAddonText}}
									@onTrigger={{this.enableAddon}}
									@size="big"
									@submitInProgress={{this.submitInProgress}}
								/>
							{{else if this.canRequestTrial}}
								<CommonRequestTrialButtonComponent
									@feature={{this.requiredBillingFeature}}
									@size="big"
								/>
							{{else}}
								<CommonUpgradePlanButtonComponent
									@feature={{this.requiredBillingFeature}}
									@text="New"
									@icon="Plus"
									@size="big"
								/>
							{{/if}}
						</div>
					{{/if}}

				</div>

				{{#unless this.isNewSectionShown}}
					<div class="empty-screen__hero">
						<img src={{this.imageUrl}} height={{this.imageHeight}} alt />
					</div>
				{{/unless}}
			{{/unless}}
		</div>
	</template>
}
